/**
 * web/overlays.ts
 * DOM modal/banner helpers for the game host screens.
 *
 * Each function mounts a single overlay inside `host` and returns a handle
 * with a `destroy()` cleanup. Overlays are mutually exclusive in practice
 * (paused / level complete / game over) but the helpers are independent
 * so the host screen can stack a Nova bark on top of a level-complete
 * card, for example.
 *
 * @module web/overlays
 */

/// <reference lib="dom" />

/** Generic overlay handle returned from each helper. */
export interface OverlayHandle {
  destroy: () => void;
}

/**
 * Backdrop element for a modal overlay.
 *
 * @param extraClass - Optional modifier class appended to the base class.
 * @returns The backdrop element, not yet attached.
 */
export const makeBackdrop = (extraClass = ''): HTMLDivElement => {
  const el = document.createElement('div');
  el.className = `sf-overlay-backdrop ${extraClass}`.trim();
  return el;
};

/**
 * Card element that holds an overlay's content.
 *
 * @param extraClass - Optional modifier class appended to the base class.
 * @returns The card element, not yet attached.
 */
export const makeCard = (extraClass = ''): HTMLDivElement => {
  const el = document.createElement('div');
  el.className = `sf-overlay-card ${extraClass}`.trim();
  return el;
};

/**
 * Overlay action button.
 *
 * @param label - Visible button text.
 * @param cls   - Full class string; defaults to the base button class.
 * @returns The button element, not yet attached.
 */
export const makeBtn = (label: string, cls = 'sf-btn'): HTMLButtonElement => {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.textContent = label;
  return b;
};

/** Format seconds as `M:SS`. */
function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Stars: 3 for 100+ seeds, 2 for 50+, 1 otherwise. */
function getStarRating(seeds: number): number {
  if (seeds >= 100) return 3;
  if (seeds >= 50) return 2;
  return 1;
}

/** Mount the pause menu overlay. Escape resumes. */
export function showPauseOverlay(
  host: HTMLElement,
  actions: { onResume: () => void; onRestart: () => void; onQuit: () => void },
): OverlayHandle {
  const backdrop = makeBackdrop('sf-pause');
  const card = makeCard();
  const title = document.createElement('h2');
  title.className = 'sf-overlay-title';
  title.textContent = 'Paused';
  const resume = makeBtn('Resume', 'sf-btn sf-btn-primary');
  const restart = makeBtn('Restart');
  const quit = makeBtn('Quit', 'sf-btn sf-btn-quit');
  resume.addEventListener('click', actions.onResume);
  restart.addEventListener('click', actions.onRestart);
  quit.addEventListener('click', actions.onQuit);
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') actions.onResume();
  };
  globalThis.addEventListener('keydown', onKey);
  card.append(title, resume, restart, quit);
  backdrop.append(card);
  host.append(backdrop);
  return {
    destroy: () => {
      globalThis.removeEventListener('keydown', onKey);
      backdrop.remove();
    },
  };
}

interface LevelCompleteOptions {
  seeds: number;
  time: number;
  score: number;
  onRestart: () => void;
  onQuit: () => void;
  onNextLevel?: () => void;
}

/** Mount the level-complete results overlay. */
export function showLevelCompleteOverlay(
  host: HTMLElement,
  opts: LevelCompleteOptions,
): OverlayHandle {
  const backdrop = makeBackdrop('sf-level-complete');
  const card = makeCard();
  const title = document.createElement('h2');
  title.className = 'sf-overlay-title';
  title.textContent = 'Level Complete';
  const stars = document.createElement('div');
  stars.className = 'sf-overlay-stars';
  stars.textContent = '★'.repeat(getStarRating(opts.seeds)) +
    '☆'.repeat(3 - getStarRating(opts.seeds));
  const stats = document.createElement('ul');
  stats.className = 'sf-overlay-stats';
  for (const [label, value] of [
    ['Seeds', `★ ${opts.seeds}`],
    ['Time', formatTime(opts.time)],
    ['Score', opts.score.toString()],
  ] as const) {
    const li = document.createElement('li');
    li.textContent = `${label}: ${value}`;
    stats.append(li);
  }
  const actions = document.createElement('div');
  actions.className = 'sf-overlay-actions';
  if (opts.onNextLevel) {
    const next = makeBtn('Next Level', 'sf-btn sf-btn-primary');
    next.addEventListener('click', opts.onNextLevel);
    actions.append(next);
  }
  const restart = makeBtn('Restart');
  restart.addEventListener('click', opts.onRestart);
  const quit = makeBtn('World Map', 'sf-btn sf-btn-quit');
  quit.addEventListener('click', opts.onQuit);
  actions.append(restart, quit);
  card.append(title, stars, stats, actions);
  backdrop.append(card);
  host.append(backdrop);
  return { destroy: () => backdrop.remove() };
}

/** Mount the game-over overlay. */
export function showGameOverOverlay(
  host: HTMLElement,
  actions: { onRestart: () => void; onQuit: () => void },
): OverlayHandle {
  const backdrop = makeBackdrop('sf-game-over');
  const card = makeCard();
  const icon = document.createElement('div');
  icon.className = 'sf-overlay-icon';
  icon.textContent = '✦';
  const title = document.createElement('h2');
  title.className = 'sf-overlay-title';
  title.textContent = 'Game Over';
  const subtitle = document.createElement('p');
  subtitle.className = 'sf-overlay-subtitle';
  subtitle.textContent = 'The stars fade a little darker…';
  const buttons = document.createElement('div');
  buttons.className = 'sf-overlay-actions';
  const restart = makeBtn('Try Again', 'sf-btn sf-btn-primary');
  restart.addEventListener('click', actions.onRestart);
  const quit = makeBtn('Menu', 'sf-btn sf-btn-quit');
  quit.addEventListener('click', actions.onQuit);
  buttons.append(restart, quit);
  card.append(icon, title, subtitle, buttons);
  backdrop.append(card);
  host.append(backdrop);
  return { destroy: () => backdrop.remove() };
}

/** Show a 2-second Nova bark above the HUD. */
export function showNovaBark(host: HTMLElement, text: string): OverlayHandle {
  const wrap = document.createElement('div');
  wrap.className = 'sf-bark';
  const bubble = document.createElement('div');
  bubble.className = 'sf-bark-bubble';
  bubble.textContent = `"${text}"`;
  wrap.append(bubble);
  host.append(wrap);
  const timer = globalThis.setTimeout(() => wrap.remove(), 2000);
  return {
    destroy: () => {
      globalThis.clearTimeout(timer);
      wrap.remove();
    },
  };
}

/** Show the simple intro cutscene as a tappable overlay. */
export function showIntroCutscene(
  host: HTMLElement,
  lines: readonly string[],
  onDone: () => void,
): OverlayHandle {
  const backdrop = makeBackdrop('sf-intro');
  const card = makeCard('sf-intro-card');
  const text = document.createElement('div');
  text.className = 'sf-intro-text';
  let idx = 0;
  const draw = (): void => { text.textContent = lines[idx] ?? ''; };
  draw();
  const next = makeBtn('Continue', 'sf-btn sf-btn-primary');
  next.addEventListener('click', () => {
    idx += 1;
    if (idx >= lines.length) {
      backdrop.remove();
      onDone();
    } else {
      draw();
    }
  });
  const skip = makeBtn('Skip', 'sf-btn sf-btn-quit');
  skip.addEventListener('click', () => {
    backdrop.remove();
    onDone();
  });
  const actions = document.createElement('div');
  actions.className = 'sf-overlay-actions';
  actions.append(next, skip);
  card.append(text, actions);
  backdrop.append(card);
  host.append(backdrop);
  return { destroy: () => backdrop.remove() };
}

